import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { SEO } from "@/components/SEO";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Loader2, Gift } from "lucide-react";
import { ProducerEventsList } from "@/components/producer/dashboard/ProducerEventsList";
import { OrdersTable } from "@/components/producer/dashboard/OrdersTable";
import { OrderDetailsSheet } from "@/components/producer/dashboard/OrderDetailsSheet";
import { toast } from "sonner";

interface ProducerEvent {
  id: string;
  title: string;
  date: string;
  location: string;
  image_url: string | null;
  status: string;
  timezone: string;
  currency: string | null;
}

interface Order {
  id: string;
  reference: string;
  user_first_name: string;
  user_last_name: string;
  user_email: string;
  phone_number_complete: string | null;
  total_amount: number;
  status: string;
  created_at: string;
  vip_ticket_quantity: number | null;
  standing_tickets: number | null;
  early_bird_tickets: number | null;
  vip_seats: string[] | null;
  promo_code: string | null;
  marketing_consent: boolean;
  terms_accepted: boolean;
}

const ProducerDashboard = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState<any>(null);
  
  // Events state
  const [events, setEvents] = useState<ProducerEvent[]>([]);
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null);
  
  // Orders state
  const [orders, setOrders] = useState<Order[]>([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [orderSheetOpen, setOrderSheetOpen] = useState(false);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session?.user) {
        navigate('/producers/login');
        return;
      }

      // Check producer role
      const { data: userRoles } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', session.user.id);

      const hasProducerRole = userRoles?.some((ur: any) => ur.role === 'producer');

      if (!hasProducerRole) {
        navigate('/producers/login?error=no_access');
        return;
      }

      setUser(session.user);
      await fetchProducerEvents(session.user.id);
    } catch (error) {
      console.error('Auth check error:', error);
      navigate('/producers/login');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchProducerEvents = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('events')
        .select('id, title, date, location, image_url, status, timezone, currency')
        .eq('producer_id', userId)
        .order('date', { ascending: false });

      if (error) throw error;
      setEvents(data || []);
    } catch (error) {
      console.error('Error fetching events:', error);
      toast.error('Failed to load events');
    }
  };

  const fetchEventOrders = async (eventId: string) => {
    setOrdersLoading(true);
    try {
      // Use the security definer function to fetch orders
      const { data, error } = await supabase
        .rpc('get_producer_event_orders', {
          p_event_id: eventId,
          p_producer_id: user?.id
        });

      if (error) {
        console.error('Error fetching orders:', error);
        throw error;
      }
      setOrders(data || []);
    } catch (error) {
      console.error('Error fetching orders:', error);
      toast.error('Failed to load orders');
    } finally {
      setOrdersLoading(false);
    }
  };

  const handleSelectEvent = (eventId: string) => {
    setSelectedEventId(eventId);
    setOrders([]);
    fetchEventOrders(eventId);
  };

  const handleBackToEvents = () => {
    setSelectedEventId(null);
    setOrders([]);
  };

  const handleSelectOrder = (order: Order) => {
    setSelectedOrder(order);
    setOrderSheetOpen(true);
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate('/producers/login');
  };

  const selectedEvent = events.find(e => e.id === selectedEventId);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <>
      <SEO 
        title="Producer Dashboard | SLAY"
        description="Manage your events, tickets, and payouts"
      />
      
      <div className="min-h-screen bg-gradient-to-br from-background via-background to-secondary/20 flex flex-col">
        <Header />
        
        <main className="flex-grow container mx-auto px-4 pt-32 pb-8 md:pt-36 md:pb-12">
          <div className="max-w-4xl mx-auto">
            {/* Header */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-foreground">
                  Producer Dashboard
                </h1>
                <p className="text-muted-foreground text-sm mt-1">
                  {user?.email}
                </p>
              </div>
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  onClick={() => navigate('/producers/comp-tickets')}
                  className="gap-2"
                >
                  <Gift className="h-4 w-4" />
                  <span className="hidden sm:inline">Comp Tickets</span>
                </Button>
                <Button variant="outline" onClick={handleSignOut}>
                  Sign Out
                </Button>
              </div>
            </div>

            {/* Content */}
            {selectedEventId ? (
              <>
                <OrdersTable
                  orders={orders}
                  eventTitle={selectedEvent?.title || ''}
                  eventId={selectedEventId}
                  currency={selectedEvent?.currency || 'AUD'}
                  onSelectOrder={handleSelectOrder}
                  onBack={handleBackToEvents}
                  isLoading={ordersLoading}
                />
                <OrderDetailsSheet
                  order={selectedOrder}
                  eventTitle={selectedEvent?.title || ''}
                  currency={selectedEvent?.currency || 'AUD'}
                  open={orderSheetOpen}
                  onOpenChange={setOrderSheetOpen}
                />
              </>
            ) : (
              <div>
                <h2 className="text-lg font-semibold text-foreground mb-4">
                  Your Events
                </h2>
                <ProducerEventsList
                  events={events}
                  onSelectEvent={handleSelectEvent}
                  selectedEventId={selectedEventId}
                />
              </div>
            )}
          </div>
        </main>
        
        <Footer />
      </div>
    </>
  );
};

export default ProducerDashboard;
